import { useQueryClient } from '@tanstack/react-query';
import { useApiQuery } from '../useApiQuery';
import { useApiMutation } from '../useApiMutation';
import { apiGet, apiPatch, apiPost, operationsClient } from '@/lib/api';
import { schedulerEndpoints } from '@/lib/api/endpoints';
import { schedulerKeys } from '@/lib/queryKeys';
import type { CreateTeamInput, Team, TeamsResponse, UpdateTeamInput } from '@/types/team';

/** All teams (with members) plus the "Unassigned" technicians. */
export function useTeams() {
  return useApiQuery<TeamsResponse>({
    queryKey: schedulerKeys.teams(),
    queryFn: () => apiGet<TeamsResponse>(operationsClient, schedulerEndpoints.teams()),
    showErrorToast: false,
  });
}

// Team membership changes also move technicians (teamId), which the dispatch
// board reads from the technician roster — so every mutation invalidates both.
function useInvalidateTeams() {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: schedulerKeys.teams() });
    void queryClient.invalidateQueries({ queryKey: schedulerKeys.technicians() });
  };
}

export function useCreateTeam() {
  const invalidate = useInvalidateTeams();
  return useApiMutation<Team, CreateTeamInput>({
    mutationFn: (input) => apiPost<Team>(operationsClient, schedulerEndpoints.teams(), input),
    onSuccess: invalidate,
  });
}

export function useUpdateTeam() {
  const invalidate = useInvalidateTeams();
  return useApiMutation<Team, { id: string } & UpdateTeamInput>({
    mutationFn: ({ id, ...patch }) =>
      apiPatch<Team>(operationsClient, schedulerEndpoints.team(id), patch),
    onSuccess: invalidate,
  });
}

export function useAssignTechnicianToTeam() {
  const invalidate = useInvalidateTeams();
  return useApiMutation<Team, { teamId: string; technicianId: string }>({
    mutationFn: ({ teamId, technicianId }) =>
      apiPost<Team>(operationsClient, schedulerEndpoints.teamAssign(teamId), { technicianId }),
    onSuccess: invalidate,
  });
}

export function useUnassignTechnicianFromTeam() {
  const invalidate = useInvalidateTeams();
  return useApiMutation<Team, { teamId: string; technicianId: string }>({
    mutationFn: ({ teamId, technicianId }) =>
      apiPost<Team>(operationsClient, schedulerEndpoints.teamUnassign(teamId), { technicianId }),
    onSuccess: invalidate,
  });
}

export function useReorderTeams() {
  const invalidate = useInvalidateTeams();
  return useApiMutation<TeamsResponse, { orderedTeamIds: string[] }>({
    mutationFn: (body) =>
      apiPatch<TeamsResponse>(operationsClient, schedulerEndpoints.teamOrder(), body),
    onSuccess: invalidate,
  });
}

export function useReorderTeamMembers() {
  const invalidate = useInvalidateTeams();
  return useApiMutation<Team, { teamId: string; orderedTechnicianIds: string[] }>({
    mutationFn: ({ teamId, orderedTechnicianIds }) =>
      apiPatch<Team>(operationsClient, schedulerEndpoints.teamMemberOrder(teamId), {
        orderedTechnicianIds,
      }),
    onSuccess: invalidate,
  });
}
